<template>

    <ui-popup
        class="ui-option-picker-popup" :style="style"
        v-show="resolvedOptions.length > 0 || placeholder" v-bind="$componentProps('ui-popup')">

        <a href="#" v-for="(option, index) in resolvedOptions" :key="option.value"
           class="option"
           :class="{
               active: optionIsActive(option),
               highlighted: highlightedIndex === index,
               divider: option.divider === true,
               multi, centered
           }"
           @click.prevent.stop="onOptionClick(option)">

            <slot name="option" :option="option">

                <ui-checkbox v-if="multi"
                             :modelValue="optionIsActive(option)" @update:modelValue="toggleOption(option, true)" />

                <div class="content">
                    <img v-if="option.image" :src="option.image" :alt="option.label">
                    <div>
                        <div class="title">{{ option.label }}</div>
                        <div v-if="option.subtitle" class="subtitle">{{ option.subtitle }}</div>
                    </div>
                </div>

            </slot>

        </a>

        <div v-if="placeholder && !resolvedOptions.length" class="placeholder" @click.stop="cancel">
            <span>{{ placeholder }}</span>
        </div>

    </ui-popup>

</template>

<script lang="ts">

import { Component, Prop, Vue, Watch } from 'vue-facing-decorator';
import { find, isArray, without } from 'lodash';

import type { CollectionOption, CollectionOptionsArgument, ComponentProps } from '@wittignl/js-core';
import { componentPropsMixin, createComponentMixin, resolveOptions } from '@wittignl/js-core';

import type { PopupProps } from './Popup.vue';
import Popup, { popupCompMixin } from './Popup.vue';

import Checkbox from '../control/Checkbox.vue';

type OnKeyDownFunction = (e: KeyboardEvent) => void;

@Component({
    name: 'ui-option-picker-popup',
    components: {
        'ui-popup': Popup,
        'ui-checkbox': Checkbox
    },
    mixins: [componentPropsMixin, popupCompMixin],
    emits: ['change', 'dismiss']
})
export default class OptionPickerPopup extends Vue {

    @Prop({ type: [Array, Object] })
    options: CollectionOptionsArgument;

    @Prop({ type: [String, Number, Boolean, Array] })
    modelValue: string | number | boolean | any[];

    @Prop({ type: Boolean })
    multi: boolean;

    @Prop({ type: Boolean })
    centered: boolean;

    @Prop({ type: Number })
    width: number;

    @Prop({ type: [Boolean, Number] })
    loading: boolean | number;

    @Prop({ type: String })
    placeholder: string;

    highlightedIndex = 0;
    boundOnKeyDown: OnKeyDownFunction;

    created() {

        this.updateHighlightedIndexFromValue();
    }

    @Watch('resolvedOptions')
    onResolvedOptionsUpdate(newOptions, oldOptions) {

        const oldHighlightedItem = this.highlightedIndex < oldOptions.length ? oldOptions[this.highlightedIndex] : null;

        if(oldHighlightedItem){
            
            const newHighlightedItem = find(newOptions, { value: oldHighlightedItem.value });
            this.highlightedIndex = newHighlightedItem ? newOptions.indexOf(newHighlightedItem) : 0;
        }
    }

    mounted() {

        this.boundOnKeyDown = this.onKeyDown.bind(this);
        document.addEventListener('keydown', this.boundOnKeyDown);

        this.$nextTick(() => {
            this.scrollToActiveItem();
        });
    }

    beforeUnmount() {

        document.removeEventListener('keydown', this.boundOnKeyDown);
    }

    updateHighlightedIndexFromValue() {

        if(this.resolvedOptions){

            const activeOption = find(this.resolvedOptions, { value: this.modelValue });
            this.highlightedIndex = activeOption ? this.resolvedOptions.indexOf(activeOption) : 0;
        }
    }

    get resolvedOptions() {

        return resolveOptions(this.options);
    }

    get style() {

        const style: any = {};

        if(this.width){
            style.width = this.width + 'px';
        }

        return style;
    }

    onKeyDown(e: KeyboardEvent) {

        const code = e.code

        if(code === 'ArrowUp'){
            this.moveHighlighted(-1);
        }
        else if(code === 'ArrowDown'){
            this.moveHighlighted(1);
        }
        else if(code === 'Enter'){
            this.commitHighlightedItem();
        }
        else {
            return;
        }

        e.preventDefault();
        e.stopPropagation();
    }

    onOptionClick(option: CollectionOption) {

        if(this.multi){
            this.toggleOption(option, !this.optionIsActive(option));
        }
        else {
            this.dismiss(option);
        }
    }

    moveHighlighted(direction: number) {

        this.highlightedIndex = Math.max(Math.min(this.highlightedIndex + direction, this.resolvedOptions.length - 1), 0);
    }

    commitHighlightedItem() {

        const option = this.highlightedIndex >= 0 ? this.resolvedOptions[this.highlightedIndex] : null;
        if(!option){
            return;
        }

        this.onOptionClick(option);
    }

    scrollToActiveItem() {

        const $contentHolderList = this.$el.getElementsByClassName('content-holder');
        const $contentHolder = $contentHolderList.length > 0 ? $contentHolderList[0] : null;

        const $activeItemList = this.$el.getElementsByClassName('active');
        const $activeItem = $activeItemList.length === 1 ? $activeItemList[0] : null;

        if(!$contentHolder || !$activeItem){
            return;
        }

        $contentHolder.scrollTop = Math.max($activeItem.offsetTop - 15, 0);
    }

    optionIsActive(option: CollectionOption) {

        if(isArray(this.modelValue)){
            return this.modelValue.includes(option.value);
        }
        else {
            return this.modelValue === option.value;
        }
    }

    toggleOption(option: CollectionOption, select: boolean) {

        if(!this.multi || !isArray(this.modelValue)){
            return;
        }

        let newValue = this.modelValue;

        if(select && !this.optionIsActive(option)){
            newValue = [...this.modelValue, option.value];
        }
        else if(!select && this.optionIsActive(option)){
            newValue = without(this.modelValue, option.value);
        }

        this.$emit('change', newValue);
    }

    dismiss(result = null) {

        this.$emit('dismiss', result);
    }

    cancel() {

        this.dismiss();
    }
}

export type OptionPickerPopupComp = InstanceType<typeof OptionPickerPopup>
export type OptionPickerPopupProps = ComponentProps<OptionPickerPopupComp> & PopupProps
export const optionPickerPopupCompMixin = createComponentMixin(OptionPickerPopup, popupCompMixin)

</script>
<style src="../../../scss/component/popup/OptionPickerPopup.scss" lang="scss"></style>